<template>
    <div class="pvef-wrap">
        <!-- CTC Info Bar -->
        <div class="pvef-ctc-bar" v-if="editEmployeeData || newCtc">
            <div class="pvef-ctc-item" v-if="editEmployeeData">
                <span class="pvef-ctc-label">Current CTC</span>
                <span class="pvef-ctc-value">₹{{ (editEmployeeData.ctc || 0).toLocaleString() }}</span>
            </div>
            <div class="pvef-ctc-divider" v-if="editEmployeeData && newCtc"></div>
            <div class="pvef-ctc-item">
                <span class="pvef-ctc-label">New CTC (approx)</span>
                <span class="pvef-ctc-value pvef-ctc-new">₹{{ (newCtc || 0).toLocaleString() }}</span>
            </div>
            <div class="pvef-ctc-divider" v-if="incrementMode"></div>
            <div class="pvef-ctc-item" v-if="incrementMode">
                <span class="pvef-ctc-label">Increment</span>
                <span class="pvef-ctc-value pvef-ctc-inc">+{{ incrementPercent }}%</span>
            </div>
        </div>

        <!-- Action Bar -->
        <div class="pvef-action-bar">
            <div class="pvef-action-label">
                <i class="fas fa-sliders-h"></i>
                {{ companyId ? 'Default Values for Employees' : 'Payhead Configuration' }}
            </div>
            <div class="pvef-action-btns">
                <button v-if="incrementMode && !companyId" class="pvef-btn pvef-btn-ghost" @click="reloadLatest">
                    <i class="fas fa-times"></i> Cancel
                </button>
                <button v-if="!companyId" class="pvef-btn pvef-btn-outline" @click="copyCompanyDefaults">
                    <i class="fas fa-copy"></i> Use Company Defaults
                </button>
                <button v-if="!companyId" class="pvef-btn pvef-btn-primary" @click="updatePayheadDefaults">
                    <i :class="processing ? 'fas fa-spinner fa-spin' : 'fas fa-save'"></i>
                    {{ processing ? 'Saving...' : 'Update Payhead Values' }}
                </button>
            </div>
        </div>

        <!-- Payhead List -->
        <div class="pvef-list">
            <div class="pvef-loading" v-if="loading">
                <i class="fas fa-circle-notch fa-spin"></i>
                <span>Loading payhead data...</span>
            </div>
            <div v-else-if="payheadMasters && payheadValues && companyDefaultsInitialized">
                <div v-for="payhead of payheads" :key="payhead.id">
                    <PayheadValueEditInput :payheads="payheads" :payhead="payhead"
                        :companyPayheadValue="companyPayheadValues && companyPayheadValues[payhead.id]"
                        :payheadValue="payheadValues[payhead.id]" :payheadMasters="payheadMasters"
                        @change="handlePayheadValueEdit" />
                </div>
            </div>
        </div>

        <!-- Bottom Save -->
        <div class="pvef-bottom-bar">
            <button class="pvef-btn pvef-btn-primary pvef-btn-lg" @click="updatePayheadDefaults">
                <i :class="processing ? 'fas fa-spinner fa-spin' : 'fas fa-money-bill-wave'"></i>
                {{ processing ? 'Please wait...' : incrementMode ? 'Confirm CTC Increment' : 'Update Payhead Values' }}
            </button>
        </div>
    </div>
</template>

<script>
import { round2 } from "../../utils/common"
import { http } from "../../utils/http"
import PayheadValueEditInput from "./PayheadValueEditInput.vue"

export default {
    props: {
        // either company or employee id required
        employeeId: { type: String, default: "" },
        companyId: { type: String, default: "" },
        editEmployeeData: { type: Object, default: null },
    },
    data() {
        return {
            newCtc: 0,
            incrementMode: false,
            incrementPercent: 0,
            payheads: [],
            loading: false,
            payheadMasters: null,
            processing: false,
            editValues: {},
            companyDefaultsInitialized: false,
            payheadValues: null,
            // if employee then its used to copy from company values
            companyPayheadValues: null,
            companyPayheadValuesOnlyEnabled: null,
        }
    },
    components: { PayheadValueEditInput },
    async mounted() {
        this.loading = true
        try {
            await this.fetchPayheads()
            await this.fetchPayheadMasters()
            await this.fetchPayheadValues()
            const incrPerc = this.$route.query.increment_percent
            this.incrementMode = !!incrPerc
            this.incrementPercent = Number(incrPerc || 0)

            if (this.companyPayheadValues) {
                this.copyCompanyDefaults(false)
            }

            // Calculate initial CTC
            this.updateNewCtc()

            // Handle CTC increment if requested
            if (this.incrementMode && this.incrementPercent && this.editEmployeeData) {
                const empPayheadValues = this.getEmpPayheadValues()
                console.log("empPayheadValues for increment", empPayheadValues)

                const currentBasicSalary = empPayheadValues.find(
                    (item) => item.payhead && item.payhead.isBasicSalary
                )

                if (currentBasicSalary) {
                    const currentCtc = this.editEmployeeData.ctc || 0
                    const additionalBasicSalaryExpected = (currentCtc * this.incrementPercent * 0.01) / 12

                    console.log(
                        "additionalBasicSalaryExpected for increment",
                        additionalBasicSalaryExpected,
                        "currentCtc",
                        currentCtc
                    )

                    const newBasicSalary = round2(
                        currentBasicSalary.value + additionalBasicSalaryExpected
                    )
                    console.log("currentBasicSalary for increment", currentBasicSalary)
                    console.log("newBasicSalary", newBasicSalary)

                    // Update the payhead value directly
                    this.payheadValues[currentBasicSalary.payhead.id].value = newBasicSalary

                    // Track the change in editValues so it gets saved
                    this.editValues[currentBasicSalary.payhead.id] = {
                        payheadId: currentBasicSalary.payhead.id,
                        value: newBasicSalary,
                        percentage: this.payheadValues[currentBasicSalary.payhead.id].percentage || 0,
                        calculationTypeId: this.payheadValues[currentBasicSalary.payhead.id].calculationTypeId,
                        calculateFromPayheadId: this.payheadValues[currentBasicSalary.payhead.id].calculateFromPayheadId || null,
                        enabled: true,
                    }

                    // Recalculate CTC with new basic salary
                    this.updateNewCtc()
                }
            }

            this.companyDefaultsInitialized = true
        } catch (err) {
            console.log("error in fetching payheads", err)
        }
        this.loading = false
    },
    methods: {
        reloadLatest() {
            window.location.href = this.$route.path + "?active_tab=PAYROLL"
        },
        handlePayheadValueEdit($event) {
            this.editValues[$event.payhead.id] = $event.data
            // console.log("handle payhead value edit", $event.data)
            this.payheadValues[$event.payhead.id].value = $event.data.value
            this.payheadValues[$event.payhead.id].percentage = $event.data.percentage
            this.updateNewCtc()
        },
        async fetchPayheadMasters() {
            const res = await http.get("/payheads/masters")
            this.payheadMasters = res.data.result
        },
        async fetchPayheads() {
            const res = await http.get("/payheads")
            this.payheads = res.data.result
        },
        async fetchPayheadValues() {
            const res = await http.get(
                `/payhead-values?employeeId=${this.employeeId}&companyId=${this.companyId}`
            )
            const payheadValueRes = res.data.result
            this.payheadValues = payheadValueRes
            this.companyPayheadValues = res.data.companyPayheadValues || null

            if (this.companyPayheadValues) {
                let temp = { ...this.companyPayheadValues }
                let final = {}
                // remove non enabled values
                for (let key in temp) {
                    if (temp[key].enabled) {
                        final[key] = temp[key]
                    }
                }
                // console.log("this.companyPayheadValuesOnlyEnabled", final)
                this.companyPayheadValuesOnlyEnabled = final
            }
        },
        async updatePayheadDefaults() {
            if (this.processing) return
            try {
                const storeCtcSnapshot = !!this.incrementPercent
                const oldCtc = this.editEmployeeData && this.editEmployeeData.ctc
                if (storeCtcSnapshot && oldCtc > this.newCtc) {
                    return this.$toast.error("New CTC must be more than old CTC!")
                }
                const updateData = Object.values(this.editValues).map((item) => ({
                    ...item,
                    employeeId: this.employeeId || null,
                    companyId: this.companyId || null,
                }))

                for (let updateItem of updateData) {
                    if (updateItem.value && updateItem.value < 0) {
                        return this.$toast.error("Value cannot be negative")
                    }
                    if (
                        updateItem.percentage &&
                        (updateItem.percentage < 0 || updateItem.percentage > 100)
                    ) {
                        return this.$toast.error("Percentage must be between 0 to 100")
                    }
                    // if (updateItem.enabled && !updateItem.calculationTypeId) {
                    //     return this.$toast.error(`Calculation type is required for enabled fields`)
                    // }
                }
                this.processing = true

                const res = await http.post("/payhead-values/create-or-update", {
                    employeeId: this.employeeId,
                    updateData,
                    storeCtcSnapshot,
                    oldCtc,
                    newCtc: this.newCtc,
                })
                window.confirm("Are you sure you want to update the payhead values? This action cannot be undone.")
                this.$toast.success("Payhead values updated")
                window.location.href = this.$route.path + "?active_tab=PAYROLL"
            } catch (err) {
                console.log("error in updating payhead defaults", err)
                this.$toast.error("Error updating payhead values")
            }
            this.processing = false
        },
        copyCompanyDefaults(overwrite = true) {
            const copiedPayheadValues = {}
            const loopArray = overwrite ? this.companyPayheadValues : this.payheadValues
            if (overwrite) {
                this.$toast.info("Values Copied! Please save to overwrite!")
            }
            for (let key in loopArray) {
                if (overwrite) {
                    copiedPayheadValues[key] = {
                        ...this.companyPayheadValues[key],
                        companyId: null,
                        employeeId: this.employeeId,
                    }
                } else {
                    // remove falsy values and add others
                    for (let key2 in this.payheadValues[key]) {
                        if (
                            !this.payheadValues[key][key2] &&
                            this.payheadValues[key][key2] !== false &&
                            this.payheadValues[key][key2] !== 0
                        ) {
                            delete this.payheadValues[key][key2]
                        }
                    }
                    copiedPayheadValues[key] = {
                        ...this.companyPayheadValues[key],
                        ...this.payheadValues[key],
                        enabled: this.companyPayheadValues[key]
                            ? this.companyPayheadValues[key].enabled &&
                            this.payheadValues[key].enabled
                            : this.payheadValues[key].enabled,
                        companyId: null,
                        employeeId: this.employeeId,
                    }
                }
            }

            if (overwrite) {
                // Force Vue re-render for user-triggered changes
                this.payheadValues = null
                setTimeout(() => {
                    this.payheadValues = copiedPayheadValues
                })
            } else {
                // Direct assignment during initialization — no re-render hack needed
                this.payheadValues = copiedPayheadValues
            }
        },
        updateNewCtc() {
            this.newCtc = this.getNewCtcValue()
        },
        getEmpPayheadValues() {
            if (!this.payheadValues) return []
            const empPayheadValues = Object.values(this.payheadValues).map((empPayheadVal) => {
                return {
                    ...empPayheadVal,
                    payhead: this.payheads.find((item) => empPayheadVal.payheadId == item.id),
                }
            })
            return empPayheadValues
        },
        getNewCtcValue() {
            // update ctc calculation....
            if (!this.payheadValues || !this.payheadMasters) return 0
            const empPayheadValues = this.getEmpPayheadValues()
            const basicSalary = empPayheadValues.find((item) => item.payhead.isBasicSalary)
            // dont' consider overtime

            console.log("empPayheadValues", empPayheadValues)

            const earningList = empPayheadValues.filter(
                (item) =>
                    item.enabled &&
                    item.payhead.PayrollPayheadType &&
                    !item.payhead.PayrollPayheadType.isDecrement &&
                    !item.payhead.isBasicSalary &&
                    !item.payhead.isOvertimePayhead
                // &&
                // item.payhead.name.toLowerCase() !== "reimbursements"
            )

            const deductionList = empPayheadValues.filter(
                (item) =>
                    item.enabled &&
                    item.payhead.PayrollPayheadType &&
                    item.payhead.PayrollPayheadType.isDecrement &&
                    !item.payhead.isBasicSalary &&
                    !item.payhead.isLeavePayhead
            )

            const earningsForCtc = earningList.reduce((val, item) => {
                if (item.percentage && item.calculateFromPayheadId) {
                    const pheadVal =
                        empPayheadValues.find(
                            (phv) => phv.payhead.id == item.calculateFromPayheadId
                        )?.value || 0
                    const amt = pheadVal * item.percentage * 0.01
                    return val + amt
                }
                return val + (item.value || 0)
            }, 0)

            // don't consider leave
            const deductionsForCtc = deductionList.reduce((val, item) => {
                if (item.percentage && item.calculateFromPayheadId) {
                    const pheadVal =
                        empPayheadValues.find(
                            (phv) => phv.payhead.id == item.calculateFromPayheadId
                        )?.value || 0
                    const amt = pheadVal * item.percentage * 0.01
                    return val + amt
                }
                return val + (item.value || 0)
            }, 0)

            console.log("earning list", earningList)
            console.log("deductions list", deductionList)
            console.log("earnings", earningsForCtc)
            console.log("deductionsForCtc", deductionsForCtc)

            if (!basicSalary) {
                console.warn("Basic salary not found in payhead values")
                return 0
            }

            return round2((basicSalary.value + earningsForCtc - deductionsForCtc) * 12)
        },
    },
}
</script>

<style lang="scss" scoped>
@import "../../assets/variables.scss";

.pvef-wrap {}

/* CTC Bar */
.pvef-ctc-bar {
    display: flex;
    align-items: center;
    gap: 0;
    background: white;
    border: 1px solid $gray100;
    border-radius: 10px;
    padding: 14px 20px;
    margin-bottom: 14px;
    box-shadow: $shadowSm;
    flex-wrap: wrap;
}

.pvef-ctc-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 0 20px;
}

.pvef-ctc-divider {
    width: 1px;
    height: 32px;
    background: $gray200;
}

.pvef-ctc-label {
    font-size: 10.5px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: .4px;
    color: $gray400;
    margin-bottom: 2px;
}

.pvef-ctc-value {
    font-size: 16px;
    font-weight: 700;
    color: $gray800;
}

.pvef-ctc-new {
    color: $primaryColor;
}

.pvef-ctc-inc {
    color: #059669;
}

/* Action Bar */
.pvef-action-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
    background: white;
    border: 1px solid $gray100;
    border-radius: 10px;
    margin-bottom: 14px;
    box-shadow: $shadowSm;
    flex-wrap: wrap;
    gap: 10px;
}

.pvef-action-label {
    font-size: 13px;
    font-weight: 700;
    color: $gray700;
    display: flex;
    align-items: center;
    gap: 6px;

    i {
        color: $primaryColor;
        font-size: 12px;
    }
}

.pvef-action-btns {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
}

/* Buttons */
.pvef-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 16px;
    border-radius: 8px;
    font-size: 12.5px;
    font-weight: 600;
    cursor: pointer;
    border: none;
    font-family: inherit;
    transition: all $transitionFast;
}

.pvef-btn-primary {
    background: $primaryColor;
    color: white;

    &:hover {
        background: $primaryDark;
    }
}

.pvef-btn-outline {
    background: white;
    color: $primaryColor;
    border: 1px solid rgba(0, 94, 137, .2);

    &:hover {
        background: $primaryLight;
    }
}

.pvef-btn-ghost {
    background: transparent;
    color: $gray500;
    border: 1px solid $gray200;

    &:hover {
        background: $gray50;
        color: $gray700;
    }
}

.pvef-btn-lg {
    padding: 10px 24px;
    font-size: 13px;
}

/* List */
.pvef-list {
    margin-bottom: 14px;
}

.pvef-loading {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 40px 20px;
    font-size: 13px;
    color: $gray400;
    background: white;
    border: 1px solid $gray100;
    border-radius: 10px;

    i {
        font-size: 16px;
        color: $primaryColor;
    }
}

/* Bottom bar */
.pvef-bottom-bar {
    display: flex;
    justify-content: flex-end;
    padding: 10px 0;
}

@media(max-width:640px) {
    .pvef-ctc-bar {
        flex-direction: column;
        gap: 10px;
    }

    .pvef-ctc-divider {
        width: 60px;
        height: 1px;
    }

    .pvef-action-bar {
        flex-direction: column;
        align-items: flex-start;
    }
}
</style>
